import { reactive, inject, computed } from 'vue';
import { type tabs } from '@shared/components/List/ListTypes';
import { type dateFilter } from '@shared/components/DateRangePicker';
import { type TextMessage, type Status } from './TextMessagesTypes';
import { loadDrafts, loadSent, loadStatisticsCount, loadScheduled, loadPaused, loadSending, loadTemplates } from './TextMessages.access';
import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
import { Permissions } from '@shared/Root/Permissions';
import { getWriteAccessDisabledIds } from '@cwd/Acl/useAclAccess';

export interface TextMessageListState {
    items: TextMessage[];
    isBusy: boolean;
    currentTab: Status;
    tabData: null | {
        description: string;
    };
    selected: string[];
    hasScope: boolean;
    tabs: tabs;
    dateFilter: dateFilter | null;
}

export function useTextMessageList() {
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);

    const state = reactive<TextMessageListState>({
        items: [],
        isBusy: false,
        currentTab: 'drafts',
        tabData: null,
        selected: [],
        hasScope: false,
        tabs: {
            drafts: { name: 'drafts', display: $Resources.SmsDraftsTab },
            scheduled: { name: 'scheduled', display: $Resources.SmsScheduledTab, hidden: true },
            paused: { name: 'paused', display: $Resources.SmsPausedTab, hidden: true },
            sending: { name: 'sending', display: $Resources.SmsSendingTab, hidden: true },
            sent: { name: 'sent', display: $Resources.SmsSentTab },
            template: { name: 'template', display: $Resources.TemplatesTab },
        },
        dateFilter: null,
    });

    const switchTab = async (tab: Status) => {
        if (state.isBusy) return;

        clearSelection();

        state.currentTab = tab;
        state.isBusy = true;
        state.items = [];
        toggleDateFilter(tab);

        loadTextMessages(state.currentTab);
    };

    const toggleDateFilter = (tab: Status) => {
        state.dateFilter = tab === 'sent' ? { start: null, end: null } : null;
    };

    const dateRangeChanged = async (start: string, end: string) => {
        if (!state.dateFilter) return;

        state.dateFilter.start = start && start !== 'Invalid Date' ? start : null;
        state.dateFilter.end = end && end !== 'Invalid Date' ? end : null;
        state.isBusy = true;

        await loadTextMessages(state.currentTab);
    };

    const load: { [key in Status]: any } = {
        drafts: loadDrafts,
        scheduled: loadScheduled,
        paused: loadPaused,
        sending: loadSending,
        sent: loadSent,
        template: loadTemplates,
    };

    let sendingTimeout: Timeout;

    const loadTextMessages = async (type: Status) => {
        state.tabData = {
            description: $Resources['Sms' + type.charAt(0).toUpperCase() + type.slice(1) + 'Introduction'],
        };

        const start = type === 'sent' ? state.dateFilter?.start : undefined;
        const end = type === 'sent' ? state.dateFilter?.end : undefined;

        await load[type](start, end)
            .then((data: TextMessage[]) => {
                state.items = data;
                clearTimeout(sendingTimeout);

                if (type === 'sending' && data.length > 0) {
                    sendingTimeout = setTimeout(function () {
                        switchTab('sending');
                    }, 5000);
                }
            })
            .finally(() => (state.isBusy = false));
    };

    const filterOptions = () => {
        return ['Subject', 'Category.Name'];
    };

    const orderOptions = () => {
        const options = [
            { display: $Resources.smsSortByLastModifiedLabel, key: 'Modified' },
            { display: $Resources.smsSortBySubjectLabel, key: 'Subject' },
            { display: $Resources.SortByCategoryLabel, key: 'Category.Name' },
        ];

        if (state.currentTab === 'sent') {
            options.push({ display: $Resources.SmsSentLabel, key: 'RecipientCount' });
        }

        return options;
    };

    const filteredItems = () => {
        return state.items.filter((item: TextMessage) => {
            return !item.Deleted;
        });
    };

    const clearSelection = () => {
        state.selected = [];
    };

    const disabledCheckboxes = computed(() =>
        getWriteAccessDisabledIds(
            filteredItems(),
            (item) => item.SmsMessageId,
            (item) => item.CurrentUserAccessLevel
        )
    );

    const initialize = async () => {
        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Sms',
            Permission: Permissions.Write,
        };

        state.hasScope = scopeAccess.hasScope(requireScopeParams);

        const location = window.location;
        const url = new URL(location.toString());
        const params = url.searchParams;

        params.get('sent') && bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertSmsScheduledForSendingBody });
        params.get('draftrestored') && bsAlert({ title: $Resources.AlertSmsAbortedHeader, content: $Resources.AlertSmsAbortedBody });

        loadStatisticsCount().then((data: { Paused: number; Scheduled: number; Sent: number }) => {
            state.tabs.paused.hidden = !data.Paused;
            state.tabs.scheduled.hidden = !data.Scheduled;
        });

        switchTab((params.get('tab') as Status) || 'drafts');
    };

    return {
        state,
        switchTab,
        loadTextMessages,
        dateRangeChanged,
        filterOptions,
        orderOptions,
        filteredItems,
        clearSelection,
        disabledCheckboxes,
        initialize,
    };
}
